<template>
  <div class="instagram-analytics-public-campaign">
    <div class="mb-x3" layout layout-align="space-between-center">
      <div layout>
        <div layout="column">
          <label class="nav_label">値1</label>
          <el-select v-model="value1" @change="changeChart">
            <el-option v-for="(item, index) in values" :key="index" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </div>
        <div class="ml-x2" layout="column">
          <label class="nav_label">値2</label>
          <el-select v-model="value2" @change="changeChart">
            <el-option v-for="(item, index) in values" :key="index" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </div>
      </div>
      <div>
        <div class="nav_label">&nbsp;</div>
        <el-switch
          v-model="isBeginAtZero"
          active-text="0から表示"
          :active-value="true"
          :inactive-value="false"
        ></el-switch>
      </div>
      <div>
        <div class="nav_label">&nbsp;</div>
        <div layout layout-align="space-around-center">
          <el-button-group>
            <el-button
              v-for="(item, index) in intervals"
              :key="index"
              :type="interval === item.value ? 'primary' : 'default'"
              @click="changeInterval(item.value)"
            >
              {{ item.label }}
            </el-button>
          </el-button-group>
          <div class="ml-x1">
            <el-tooltip placement="top" content="CSVダウンロード">
              <CsvDownload :download="downloadCsv" :text="''"></CsvDownload>
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>
    <div class="message message-info" v-if="!campaignAnalytics.length">データがありません。</div>
    <div class="message message-info" v-else-if="value1 === '' && value2 === ''">値1または値2を選択してください。</div>
    <div class="body" v-else>
      <AnalyticsChartVertical
        ref="chart"
        :data="chartData"
        :xAxis="labels"
        :interval="interval"
        :isBeginAtZero="isBeginAtZero"
      ></AnalyticsChartVertical>
    </div>
  </div>
</template>

<script lang="ts">
import moment from 'moment-timezone'
import { storeToRefs } from 'pinia'
import { computed, defineComponent, ref, watch } from 'vue'

import AnalyticsChartVertical from '@/client/components/_atoms/analytics_chart_vertical'
import CsvDownload from '@/client/components/_atoms/csv_download'
import { useInstagramInstantwinAnalyticsPublicStore } from '@/client/features/instagram_instantwin_analytics_public/hooks/instagramInstantwinAnalyticsPublicStore'

type TimeRange = { start: number; end: number }

type AnalyticsData = {
  applicant: (number | null)[]
  follower: (number | null)[]
  entry: (number | null)[]
  newApplicant: (number | null)[]
  winner: (number | null)[]
}

type ChartDataItem = { metric: string; label: string | undefined; data: (number | null)[] | null }

export default defineComponent({
  components: {
    CsvDownload,
    AnalyticsChartVertical
  },
  setup() {
    const store = useInstagramInstantwinAnalyticsPublicStore()
    const { campaignAnalytics, campaign } = storeToRefs(store)

    const chartData = ref<ChartDataItem[]>([])
    const labels = ref<string[]>([])
    const interval = ref('1_day')
    const value1 = ref('follower')
    const value2 = ref('applicant')
    const isBeginAtZero = ref(false)

    const values = computed(() => {
      return [
        { label: '指定なし', value: '' },
        { label: '応募数', value: 'entry' },
        { label: '応募者数', value: 'applicant' },
        { label: '新規応募者数', value: 'newApplicant' },
        { label: '当選数', value: 'winner' },
        { label: 'フォロワー', value: 'follower' }
      ]
    })

    const intervals = computed(() => {
      return [
        { label: '1時間', value: 'hour' },
        { label: '1日', value: '1_day' },
        { label: '7日', value: '7_day' },
        { label: '1ヶ月', value: '1_month' }
      ]
    })

    const timeRange = computed((): TimeRange[] => {
      const timeRangeData: TimeRange[] = []

      if (!campaignAnalytics.value.length) {
        return timeRangeData
      }

      const addNumber = Number(interval.value.match(/\d+/g))

      let timeEnd
      let timeStart = moment(moment(campaignAnalytics.value[0].datetime).format('YYYY-MM-DD 00:00:00')).unix()

      const endDateUnix = moment(
        moment(campaignAnalytics.value[campaignAnalytics.value.length - 1].datetime).format('YYYY-MM-DD HH:mm:00')
      ).unix()

      while (timeStart < endDateUnix) {
        if (
          interval.value === '1_day' ||
          interval.value === '7_day' ||
          interval.value === '14_day' ||
          interval.value === '28_day'
        ) {
          // 日数のunix値: 日数 x 一日のunix値 (一日のunix値: 86400)
          timeEnd = timeStart + addNumber * 86400 - 1
        } else if (interval.value === '1_month') {
          timeEnd = moment.unix(timeStart).add(addNumber, 'months').startOf('month').unix() - 1
        }

        if (timeEnd > endDateUnix) {
          timeEnd = endDateUnix
        }

        timeRangeData.push({
          start: timeStart,
          end: timeEnd
        })

        timeStart = timeEnd + 1
      }

      if (timeRangeData.length) {
        timeRangeData[0].start = moment(campaignAnalytics.value[0].datetime).unix()
      } else {
        timeRangeData.push({ start: endDateUnix, end: endDateUnix })
      }

      return timeRangeData
    })

    const timeRangeForLabel = computed((): TimeRange[] => {
      const timeRangeData: TimeRange[] = []

      if (!campaignAnalytics.value.length) {
        return timeRangeData
      }

      const addNumber = Number(interval.value.match(/\d+/g))

      let timeEnd
      let timeStart = moment(moment(campaignAnalytics.value[0].datetime).format('YYYY-MM-DD')).unix()
      const endDateUnix = moment(
        moment(campaignAnalytics.value[campaignAnalytics.value.length - 1].datetime).format('YYYY-MM-DD HH:mm:00')
      ).unix()

      while (timeStart < endDateUnix) {
        if (
          interval.value === '1_day' ||
          interval.value === '7_day' ||
          interval.value === '14_day' ||
          interval.value === '28_day'
        ) {
          // 日数のunix値: 日数 x 一日のunix値 (一日のunix値: 86400)
          timeEnd = timeStart + addNumber * 86400 - 1
        } else if (interval.value === '1_month') {
          timeEnd = moment.unix(timeStart).add(addNumber, 'months').startOf('month').unix() - 1
        }

        if (timeEnd > endDateUnix) {
          timeEnd = endDateUnix
        }

        timeRangeData.push({
          start: timeStart,
          end: timeEnd
        })

        timeStart = timeEnd + 1
      }

      if (timeRangeData.length) {
        timeRangeData[0].start = moment(campaignAnalytics.value[0].datetime).unix()
      } else {
        timeRangeData.push({ start: endDateUnix, end: endDateUnix })
      }

      return timeRangeData
    })

    const analytics = computed((): AnalyticsData => {
      const data: AnalyticsData = {
        applicant: [],
        follower: [],
        entry: [],
        newApplicant: [],
        winner: []
      }

      if (!campaignAnalytics.value.length) {
        return data
      }

      if (interval.value === 'hour') {
        const lastIndex = campaignAnalytics.value.length - 1

        campaignAnalytics.value.forEach((value, index) => {
          if (index !== lastIndex) {
            data.follower.push(value.follower_count)
            data.applicant.push(value.applicant_count)
            data.entry.push(value.entry_count)
            data.newApplicant.push(value.new_applicant_count)
            data.winner.push(value.winner_count)
          }
        })
        return data
      }

      const endDateUnix = moment(campaignAnalytics.value[campaignAnalytics.value.length - 1].datetime)
        .startOf('minute')
        .unix()

      timeRange.value.forEach((time, index) => {
        // 該当する期間の分析データを取得
        let analyticsData = campaignAnalytics.value
          .filter(analytic => {
            return moment(analytic.datetime).unix() >= time.start && moment(analytic.datetime).unix() <= time.end
          })
          .map(analytic => analytic)

        if (interval.value === '1_day') {
          const momentDate = index < timeRange.value.length - 1 ? moment.unix(time.start) : moment.unix(time.end)
          const datetime = momentDate.startOf('hour').format('YYYY-MM-DD HH:mm')

          data.follower.push(getValueByHour(analyticsData, datetime, 'follower_count'))
        } else {
          data.follower.push(getValueByPeriod(analyticsData, 'follower_count'))
        }

        if (time.end === endDateUnix) {
          analyticsData = analyticsData.filter(v => moment(v.datetime).unix() !== endDateUnix)
        }

        data.applicant.push(getTotal(analyticsData, 'applicant_count'))
        data.entry.push(getTotal(analyticsData, 'entry_count'))
        data.newApplicant.push(getTotal(analyticsData, 'new_applicant_count'))
        data.winner.push(getTotal(analyticsData, 'winner_count'))
      })

      return data
    })

    const addition = (numbers: number[] = []): number | null => {
      const valuesData = numbers.filter(v => v !== null)

      if (!valuesData.length) {
        return null
      }

      return numbers.map(v => toNumber(v)).reduce((a, c) => a + c, 0)
    }

    const changeChart = () => {
      chartData.value = getChartData()
      labels.value = getLabels()
    }

    const changeInterval = (nextInterval: string): void => {
      interval.value = nextInterval
      changeChart()
    }

    const downloadCsv = (): { filename: string; data: any } => {
      const filename = campaign.value ? `${campaign.value.title}_分析` : '分析'

      const data: (string | number | null)[][] = []

      data.push(['日付', '応募数', '応募者数', '新規応募者数', '当選数', 'フォロワー'])

      labels.value.forEach((label, index) => {
        let labelText = label

        if (interval.value === '1_day') {
          labelText = moment(labelText).startOf('hour').format('YYYY/MM/DD')
        }

        const applicant = isNaN(Number(analytics.value.applicant[index])) ? null : analytics.value.applicant[index]
        const follower = isNaN(Number(analytics.value.follower[index])) ? null : analytics.value.follower[index]
        const entry = isNaN(Number(analytics.value.entry[index])) ? null : analytics.value.entry[index]
        const newApplicant = isNaN(Number(analytics.value.newApplicant[index]))
          ? null
          : analytics.value.newApplicant[index]
        const winner = isNaN(Number(analytics.value.winner[index])) ? null : analytics.value.winner[index]

        data.push([labelText, entry, applicant, newApplicant, winner, follower])
      })

      return { filename, data }
    }

    const getChartData = (): ChartDataItem[] => {
      const data: ChartDataItem[] = []

      if (value1.value) {
        const label = values.value.find(value => value.value === value1.value)
        data.push({
          metric: value1.value,
          label: label?.label,
          data: analytics.value[value1.value]
        })
      } else {
        data.push({
          metric: '',
          label: '',
          data: null
        })
      }

      if (value2.value) {
        const label = values.value.find(value => value.value === value2.value)
        data.push({
          metric: value2.value,
          label: label?.label,
          data: analytics.value[value2.value]
        })
      }

      return data
    }

    const getLabels = (): any => {
      let labelsData: string[] = []

      switch (interval.value) {
        case 'hour':
          labelsData = []
          campaignAnalytics.value.slice(0, -1).forEach(value => {
            const date = moment(value.datetime).startOf('hour').format('YYYY/MM/DD HH:mm')
            labelsData.push(date)
          })
          return labelsData
        case '1_day':
          return timeRangeForLabel.value.map((time, index) => {
            if (index === timeRangeForLabel.value.length - 1) {
              return moment.unix(time.end).startOf('hour').format('YYYY/MM/DD')
            }

            return moment.unix(time.start).startOf('hour').format('YYYY/MM/DD')
          })
        case '7_day':
        case '14_day':
        case '28_day':
          return timeRangeForLabel.value.map(time => {
            return `${moment.unix(time.start).format('MM/DD')}-${moment.unix(time.end).format('MM/DD')}`
          })
        case '1_month':
          return timeRangeForLabel.value.map(time => {
            return moment.unix(time.start).format('YYYY/MM')
          })
      }
    }

    const getMetricArray = (insights: any[], metric: string): any[] => {
      return insights.map(insight => getMetricValue(insight, metric))
    }

    const getMetricValue = (insight: any, metric: string): any | null => {
      return isNaN(insight[metric]) ? null : insight[metric]
    }

    const getValueByPeriod = (analyticsData: any[], value: string): number | null => {
      const data = analyticsData.filter(analytic => analytic[value] !== null)

      if (data.length === 0) {
        return null
      }

      // If there is only one period, return last value
      if (analyticsData.length === campaignAnalytics.value.length) {
        return data[data.length - 1][value]
      }

      // If this is the first period, return first value
      if (moment(analyticsData[0].datetime).isSame(campaignAnalytics.value[0].datetime)) {
        return data[0][value]
      }

      return data[data.length - 1][value]
    }

    const getValueByHour = (analyticsData: any[], datetime: string, value: string): number | null => {
      const data = analyticsData.find(analytic => moment(analytic.datetime).isSame(datetime, 'hour'))

      if (!data) {
        return null
      }

      return data[value]
    }

    const toNumber = (value: any): number => {
      return !isNaN(value) ? value : 0
    }

    const getTotal = (data: any, metric: string): number | null => {
      return addition(getMetricArray(data, metric))
    }

    watch(analytics, () => {
      changeChart()
    })

    return {
      campaignAnalytics,
      chartData,
      labels,
      interval,
      value1,
      value2,
      isBeginAtZero,
      values,
      intervals,
      changeChart,
      changeInterval,
      downloadCsv
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.instagram-analytics-public-campaign {
  .body {
    margin: 0 ($spacing-unit * 3);
  }
}
</style>
